import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const ContentTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '', template_type: 'email', subject_template: '', body_template: ''
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/marketing/content/templates');
      setTemplates(response.data.templates || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setCurrentTemplate(null);
    setFormData({ name: '', template_type: 'email', subject_template: '', body_template: '' });
    setShowForm(true);
  };

  const handleEdit = (template) => {
    setCurrentTemplate(template);
    setFormData({
      name: template.name,
      template_type: template.template_type || 'email',
      subject_template: template.subject_template || '',
      body_template: template.body_template || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/marketing/content/templates/${id}`);
      setSuccess('Template deleted');
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentTemplate) {
        await api.put(`/marketing/content/templates/${currentTemplate.id}`, formData);
        setSuccess('Template updated');
      } else {
        await api.post('/marketing/content/templates', formData);
        setSuccess('Template created');
      }
      setShowForm(false);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
  };

  if (showForm) {
    return (
      <div>
        {/* Breadcrumb Navigation */}
        <nav className="breadcrumb">
          <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
          <span className="breadcrumb-separator">/</span>
          <button onClick={() => setShowForm(false)} className="breadcrumb-link">Content Templates</button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{currentTemplate ? 'Edit Template' : 'New Template'}</span>
        </nav>

        <div className="card">
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '1.5rem',
            borderBottom: '3px solid #EF4444'
          }}>
            <div>
              <h2 style={{
                color: 'var(--gray-900)',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                margin: '0 0 0.25rem 0'
              }}>
                {currentTemplate ? 'Edit Content Template' : 'Create Content Template'}
              </h2>
              <p style={{
                color: 'var(--gray-600)',
                margin: 0,
                fontSize: '0.875rem'
              }}>
                {currentTemplate ? 'Update template details' : 'Create a new reusable content template'}
              </p>
            </div>
          </div>

          {error && (
            <div style={{
              backgroundColor: 'var(--red-50)',
              border: '1px solid var(--red-200)',
              color: 'var(--red-800)',
              padding: '0.75rem',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Template Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Welcome Email Template"
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: 'var(--gray-700)',
                  fontSize: '0.875rem'
                }}>
                  Template Type *
                </label>
                <select
                  required
                  value={formData.template_type}
                  onChange={(e) => setFormData({...formData, template_type: e.target.value})}
                  className="form-input"
                >
                  <option value="email">Email</option>
                  <option value="social_post">Social Post</option>
                  <option value="blog_post">Blog Post</option>
                  <option value="ad_copy">Ad Copy</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="landing_page">Landing Page</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Subject Template
              </label>
              <input
                type="text"
                value={formData.subject_template}
                onChange={(e) => setFormData({...formData, subject_template: e.target.value})}
                className="form-input"
                placeholder="e.g., Welcome to {{company_name}}, {{first_name}}!"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                Use variables like {`{{first_name}}`}, {`{{company_name}}`}, etc.
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: 'var(--gray-700)',
                fontSize: '0.875rem'
              }}>
                Body Template *
              </label>
              <textarea
                required
                rows="12"
                value={formData.body_template}
                onChange={(e) => setFormData({...formData, body_template: e.target.value})}
                className="form-input"
                placeholder="Enter your template content here. Use variables like {{first_name}}, {{product_name}}, etc."
                style={{ fontFamily: 'monospace' }}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                Use variables in double curly braces: {`{{variable_name}}`}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid var(--gray-200)' }}>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                {currentTemplate ? 'Update Template' : 'Create Template'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Content Templates</span>
      </nav>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              📝 Content Templates
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Create and manage reusable content templates
            </p>
          </div>
          <button onClick={handleCreate} className="btn btn-primary">➕ Add Template</button>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ backgroundColor: 'var(--success-50)', border: '1px solid var(--success-500)', color: 'var(--success-700)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setSuccess(null)}>
            {success}
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Type</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Usage</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 ? (
              <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>No templates found</td></tr>
            ) : (
              templates.map((template) => (
                <tr key={template.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <td style={{ padding: '0.75rem' }}>{template.name}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <span className="badge badge-info">{template.template_type}</span>
                  </td>
                  <td style={{ padding: '0.75rem' }}>{template.usage_count || 0}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <span className={`badge badge-${template.is_active ? 'success' : 'secondary'}`}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button onClick={() => handleEdit(template)} className="btn btn-sm btn-outline" style={{ marginRight: '0.5rem' }}>Edit</button>
                    <button onClick={() => handleDelete(template.id)} className="btn btn-sm btn-danger">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ContentTemplates;
